import React, { Fragment, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/react';
import { t } from '@lingui/macro';
import { useLocalisation } from '@3plearning/lingui-cdn';

// Components
import Checkbox, { LABEL_STYLES } from '../../checkbox/checkbox';
import Button, { BUTTON_STYLES } from '../../button/button';
import GroupModal from '../group-modals/group-modal';

// Context
import CourseInformationContext from '../context/course-information-context';

// Helpers
import {
    stripCharacters, resetGroupSectionScroll, getDefaultGroupName
} from '../../../helpers/utils';
import { DEFAULT_GROUP_MODAL_ID, MAX_GROUP_NAME_DISPLAY } from '../../../helpers/enums';
import ImageAsset from '../../image-asset/image-asset';
import EditGroupContainer from '../group-modals/edit-group-modal';

function GroupSelectionItem(props) {
    const { i18n } = useLocalisation();

    // Props
    const {
        productId,
        classGroupItem,
        showModal,
        index,
        groupModalVisibility,
        groupModalId,
        editGroupHandler,
        deleteGroupHandler,
        updateGroupCheckbox,
        setShownPassword,
        showNewCourses,
        newCoursesAvailabilityCriteria
    } = props;

    const labelCheckboxRef = useRef(null);

    // Variables
    const validUid = stripCharacters(classGroupItem.name, '*').replace(/\s/gi, '-');
    const currentGroupId = classGroupItem.groupId;
    const isShowAllGroup = currentGroupId === 0;
    const [hasEdited, setHasEdited] = useState(false);

    // Context Variables
    const {
        // Select Options
        // - Country
        setCountryHandler,
        countrySelectValue,
        countrySelectOptions,
        // - Year
        setYearHandler,
        yearSelectValue,
        yearSelectOptions,
        // - Course
        setCourseHandler,
        courseSelectValue,
        courseSelectOptions,
        // - UPF Course
        setUPFCourseHandler,
        upfCourseSelectValue,
        upfCourseSelectOptions,
        // New Courses
        setNewCourseHandler,
        newCourseSelectValue,
        newCourseSelectOptions
    } = useContext(CourseInformationContext);

    // Handlers
    const showCurrentEditGroupModal = () => {
        showModal({
            modalId: classGroupItem.groupId,
            setEditMode: true
        });

        // In case if it's been opened, reset the hasEdited field
        if (hasEdited === true) {
            setHasEdited(false);
        }
    };
    const groupFilterHandler = (e) => {
        const isSelected = e.target.checked;
        const checkboxDataset = e.target.dataset;
        setShownPassword('');
        updateGroupCheckbox({
            value: checkboxDataset.value,
            isSelected
        });
    };
    const closeHandler = () => {
        resetGroupSectionScroll();
        setCountryHandler('');
        setYearHandler('');
        showModal({ modalId: DEFAULT_GROUP_MODAL_ID, isClosing: true });
    };
    const localEditGroupHandler = ({ name }) => {
        let newEditGroupName = name;

        let groupName = classGroupItem.name;
        if (classGroupItem.isDefault) {
            groupName = getDefaultGroupName(groupName);
        }
        /**
         * Check if the existing name and the new name are the same,
         * and if they are set the newGroupName to be undefined
         */
        if (name === groupName) {
            newEditGroupName = undefined;
        }

        return editGroupHandler({
            name: newEditGroupName,
            groupId: currentGroupId
        });
    };
    const localDeleteHandler = async () => {
        const callbackResponse = await deleteGroupHandler({
            groupId: currentGroupId
        });

        // As long as the callback is a function then close the modal and execute the function
        if (typeof callbackResponse === 'function') {
            await closeHandler();
            await callbackResponse();
            return true;
        }

        return callbackResponse;
    };
    const handleChange = (value, isCourse, isUPFCourse) => {
        if (isCourse && value !== '') {
            setHasEdited(true);
        } else if (!isCourse && value === '') {
            setHasEdited(false);
        } else if (isUPFCourse && value !== '') {
            setHasEdited(true);
        } else {
            setHasEdited(false);
        }
    };
    const localSetCountryHandler = (e) => {
        const countryValue = e.target.value;
        handleChange(countryValue);
        setCountryHandler(countryValue);
    };
    const localSetYearHandler = (e) => {
        const yearValue = e.target.value;
        handleChange(yearValue);
        setYearHandler(yearValue);
    };
    const localSetCourseHandler = (e) => {
        const courseValue = e.target.value;
        const isCourse = true;
        handleChange(courseValue, isCourse);
        setCourseHandler(courseValue);
    };
    const localSetUPFCourseHandler = (e) => {
        const upfCourseValue = e.target.value;
        const isUPFCourse = true;
        handleChange(upfCourseValue, isUPFCourse);
        setUPFCourseHandler(upfCourseValue);
    };

    const localSetNewCourseHandler = (e) => {
        const newCourseValue = e.target.value;
        setHasEdited(true);
        setNewCourseHandler(newCourseValue);
    };

    const parentClasses = `checkbox__container--groups${isShowAllGroup ? ' checkbox__container--groups-all' : ''}`;

    // Text truncation methods and variables
    const checkboxTitle = classGroupItem.name;
    const classGroupId = classGroupItem.groupId;

    let initialName = classGroupItem.name;
    let labelText = checkboxTitle;

    if (classGroupItem.isDefault) {
        // remove the asterisk from the default group if it's the default
        initialName = getDefaultGroupName(initialName);
    }

    if (isShowAllGroup) {
        labelText = <Trans id={'Groups - Show All - label'} />;
    } else if (labelText.length > MAX_GROUP_NAME_DISPLAY) {
        labelText = `${labelText.substring(0, MAX_GROUP_NAME_DISPLAY)}...`;
    }

    // Course Specific props to handle the data/display
    const courseSpecificProps = {
        // Countries
        setCountryHandler: localSetCountryHandler,
        countrySelectValue,
        countrySelectOptions,
        // Years/Grades
        setYearHandler: localSetYearHandler,
        yearSelectValue,
        yearSelectOptions,
        // Courses
        setCourseHandler: localSetCourseHandler,
        courseSelectValue,
        courseSelectOptions,
        // - UPF Course
        setUPFCourseHandler: localSetUPFCourseHandler,
        upfCourseSelectValue,
        upfCourseSelectOptions,
        // New Courses
        setNewCourseHandler: localSetNewCourseHandler,
        newCourseSelectValue,
        newCourseSelectOptions
    };

    return (
        <div style={{ display: 'flex' }}>
            {!groupModalVisibility &&
                <Checkbox
                    uid={`${validUid}-groupFilter-${index}`}
                    inputType={LABEL_STYLES.visible}
                    labelForwardRef={labelCheckboxRef}
                    labelText={labelText}
                    callback={groupFilterHandler}
                    value={classGroupItem.groupId}
                    isChecked={classGroupItem.isSelected}
                    labelProps={labelText !== checkboxTitle ? { title: checkboxTitle } : false}
                    numberOfStudents={classGroupItem.studentCount}
                    isGroupCheckbox
                    parentClasses={parentClasses}>
                    {
                        !isShowAllGroup ? (
                            <Fragment>
                                <div className="groups__group-edit-container">
                                    <div className="groups__group-student-count-container">
                                        <ImageAsset
                                            type={'personIcon'}
                                            parentTitle={'person-icon'}
                                            parentClass={'groups__group-student-count'}
                                        />
                                        <div className="groups__group-count">
                                            {classGroupItem.studentCount}
                                        </div>
                                    </div>
                                    <Button
                                        buttonStyle={BUTTON_STYLES.iconText}
                                        iconName="editIcon"
                                        imageClassName="editIcon-image"
                                        visibleText={'Edit'}
                                        ariaProps={{ 'aria-label': i18n._(t`Groups - Edit group`) }}
                                        importedClasses="button--edit-group"
                                        callback={showCurrentEditGroupModal}
                                    />
                                </div>
                            </Fragment>
                        ) : ''
                    }
                </Checkbox>
            }
            {
                groupModalId === currentGroupId && groupModalVisibility ? (
                    <EditGroupContainer
                        title={<Trans id="Group Modal - edit - title" />}
                        closeHandler={closeHandler}
                        parentClasses="modal--edit-groups">
                        <GroupModal
                            inputId={`edit-group-input${classGroupId}`}
                            initialName={initialName}
                            currentId={classGroupId}
                            productId={productId}
                            saveHandler={localEditGroupHandler}
                            closeHandler={closeHandler}
                            deleteHandler={localDeleteHandler}
                            showDeleteButton={classGroupItem.isDefault === false}
                            hasEdited={hasEdited}
                            showNewCourses={showNewCourses}
                            newCoursesAvailabilityCriteria={newCoursesAvailabilityCriteria}
                            isNewGroup={false}
                            {...courseSpecificProps}
                        />
                    </EditGroupContainer>
                ) : ''
            }

        </div>
    );
}

GroupSelectionItem.propTypes = {
    classGroupItem: PropTypes.object.isRequired,
    showModal: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired,
    groupModalVisibility: PropTypes.bool.isRequired,
    productId: PropTypes.number.isRequired,
    groupModalId: PropTypes.number.isRequired,
    editGroupHandler: PropTypes.func.isRequired,
    deleteGroupHandler: PropTypes.func.isRequired,
    updateGroupCheckbox: PropTypes.func.isRequired,
    setShownPassword: PropTypes.func,
    showNewCourses: PropTypes.bool,
    newCoursesAvailabilityCriteria: PropTypes.object
};

export default GroupSelectionItem;



// WEBPACK FOOTER //
// ./components/groups/item/group-selection-item.js